export interface ProjectImage {
  src: string;
  thumbnail: string;
  alt: string;
}

export interface Project {
  slug: string;
  title: string;
  subtitle: string;
  /** Sector de negocio del cliente */
  location: string;
  category: string;
  year: string;
  /** Stack tecnológico principal */
  area: string;
  description: string;
  heroImage: string;
  images: ProjectImage[];
  /** URLs externas opcionales */
  urls?: { label: string; href: string }[];
  // ── Parrilla "Desarrollos a medida" (/a-medida) ─────────────────────────
  /** Tipo de desarrollo (filtro de la parrilla). */
  tipo: string;
  /** Pieza del logo: sólida o en contorno (acento de la tarjeta). */
  variante: 'solid' | 'outline';
  /** Foto de la tarjeta (assets/img/casos). */
  cardImage: string;
  /** Resumen corto para la tarjeta de la parrilla. */
  resumen: string;
  /** Contenido de la ficha de caso (/a-medida/:slug). */
  ficha: CaseFicha;
}

export interface FichaModulo {
  label: string;
  /** Color de la pieza del módulo (var(--ts-piece-*)). */
  color: string;
  desc: string;
}

export interface FichaCapture {
  image: string;
  caption: string;
}

/**
 * Contenido de una ficha de caso. Misma plantilla para los 6 casos; solo cambia
 * el contenido (análogo a `solutions.data.ts`). Sin "Stack" ni año (fuera jerga).
 */
export interface CaseFicha {
  heroTitle: string;
  heroSubtitle: string;
  /** Solución de sector asociada; si existe, enlace "Ver la solución de X". */
  solutionSlug?: string;
  sectorLabel?: string;
  heroImage: string;
  necesitaban: string;
  montamos: string;
  dentroIntro: string;
  /** Descripción de la Base Esencial en este caso. */
  baseDesc: string;
  modulos: FichaModulo[];
  /** Pieza construida a medida (se pinta en contorno sobre card punteada). */
  aMedida: { label: string; desc: string };
  capturas: FichaCapture[];
  /** Frase de resultado (bajo chip "Ejemplo · pendiente de testimonio real"). */
  resultado: string;
}

export const PROJECTS: Project[] = [
  // ── Self-storage / Trasteros ─────────────────────────────────────────
  {
    slug: 'gestion-trasteros-self-storage',
    title: 'Gestión de Trasteros',
    tipo: 'Plataforma + web propia',
    variante: 'solid',
    cardImage: 'assets/img/soluciones/productos-2.png',
    resumen:
      'El cliente reserva su box desde la web, firma el contrato a distancia y las cuotas se cobran solas. El equipo administrativo casi no toca el alta.',
    ficha: {
      heroTitle:
        'El trastero se reserva y se firma desde la web. La ocupación se ve sola.',
      heroSubtitle:
        'Un centro de self-storage montó su alta online: el cliente reserva su box, firma el contrato a distancia y las cuotas se cobran solas, mientras el equipo ve la ocupación real al momento.',
      solutionSlug: 'trasteros-self-storage',
      sectorLabel: 'Trasteros',
      heroImage: 'assets/img/soluciones/productos-2.png',
      necesitaban:
        'Cada alta era una visita, una llamada y un contrato en papel. La ocupación vivía en una hoja de cálculo que nunca estaba al día, y los recibos se perseguían mes a mes.',
      montamos:
        'Una web conectada a su ocupación real y un portal donde el cliente reserva, firma y paga solo. Los contratos y las cuotas recurrentes se generan sin que la oficina toque nada.',
      dentroIntro:
        'Módulos del catálogo más las piezas construidas a medida para su operativa. Todo sobre la misma plataforma: sin migrar datos, sin sistemas paralelos.',
      baseDesc:
        'Fichaje, facturación, clientes y gastos. La base sobre la que se apoyan el alta y el cobro.',
      modulos: [
        {
          label: 'Portal del cliente',
          color: 'var(--ts-piece-portal)',
          desc: 'El cliente reserva, firma su contrato y consulta sus recibos sin pasar por la oficina.',
        },
        {
          label: 'Web y CMS',
          color: 'var(--ts-piece-web)',
          desc: 'Su web de captación conectada: la disponibilidad que se ve fuera es la real.',
        },
      ],
      aMedida: {
        label: 'A su medida',
        desc: 'Plano de ocupación y cuotas recurrentes: reserva de box, alta y cobro mensual, hechos para su forma de operar.',
      },
      capturas: [
        {
          image: 'assets/img/soluciones/productos-2.png',
          caption: 'El catálogo de boxes con su disponibilidad real.',
        },
        {
          image: 'assets/img/soluciones/clientes.png',
          caption: 'Cada cliente con su contrato y sus recibos, sin papel.',
        },
      ],
      resultado:
        'De dedicar la mañana a altas y llamadas de cobro a que el cliente se dé de alta solo — y ver la ocupación de un vistazo, sin cruzar hojas.',
    },
    subtitle:
      'Web informativa con integración de TPV y de gestión integral de self-storage',
    location: 'Self-storage / Trasteros',
    category: 'Todo Sector + Web',
    year: '2025',
    area: 'Angular · Node.js · MySQL',
    description:
      'Web informativa con integración de TPV y de gestión integral de self-storage para una empresa de trasteros. Está integrada totalmente con nuestra plataforma Todo Sector, con los módulos diseñados para empresas de self-storage que necesitan digitalizar su operativa completa: desde la reserva de un espacio hasta la generación automática de facturas, clientes y el control de accesos.\n\nEl sistema permite a cada cliente (empresa de trasteros) operar con su propia instancia aislada, sus tarifas y sus contratos personalizados. La integración con pasarela de pago y firma digital elimina prácticamente el trabajo manual del equipo administrativo.',
    heroImage: 'assets/img/trasteros@700w.webp',
    images: [
      {
        src: 'assets/img/trasteros@700w.webp',
        thumbnail: 'assets/img/trasteros@700w.webp',
        alt: 'Panel de gestión de trasteros',
      },
      {
        src: 'assets/img/unsplash-hero-office.jpg',
        thumbnail: 'assets/img/unsplash-hero-office.jpg',
        alt: 'Gestión administrativa',
      },
      {
        src: 'assets/img/unsplash-metodologia.jpg',
        thumbnail: 'assets/img/unsplash-metodologia.jpg',
        alt: 'Metodología de desarrollo',
      },
    ],
  },

  // ── Gestor de obras ──────────────────────────────────────────────────
  {
    slug: 'gestor-obras-construccion',
    title: 'Gestor de Obras',
    tipo: 'Módulos hechos a medida',
    variante: 'outline',
    cardImage: 'assets/img/soluciones/proyectos.png',
    resumen:
      'Partes de obra firmados a pie de tajo y un panel donde dirección compara el coste real de cada obra con su presupuesto, sin esperar al cierre de mes.',
    ficha: {
      heroTitle:
        'Los partes se firman a pie de tajo. Dirección ve el coste real de cada obra.',
      heroSubtitle:
        'Una constructora con varios equipos en calle dejó el papel: partes de obra diarios desde el móvil, materiales y horas registrados en el sitio, y cada obra comparada con su presupuesto en tiempo real.',
      solutionSlug: 'construccion-obra',
      sectorLabel: 'Construcción',
      heroImage: 'assets/img/soluciones/proyectos.png',
      necesitaban:
        'Los partes llegaban en papel al final de la semana — o no llegaban. La oficina los pasaba a limpio para poder facturar, y nadie sabía si una obra estaba ganando o perdiendo dinero hasta el cierre de mes.',
      montamos:
        'El seguimiento completo de sus obras sobre la plataforma: partes diarios con firma en campo, control de materiales, asignación de operarios y un panel donde dirección compara lo gastado con lo presupuestado, obra a obra y al momento.',
      dentroIntro:
        'Módulos del catálogo más las piezas construidas a medida para su forma de trabajar. Todo sobre la misma plataforma: sin migrar datos, sin sistemas paralelos.',
      baseDesc:
        'Fichaje, facturación, clientes y gastos. Cada operario ficha desde su móvil, sin licencia por cabeza.',
      modulos: [
        {
          label: 'Partes de trabajo',
          color: 'var(--ts-piece-partes)',
          desc: 'Partes de obra diarios con materiales, horas y firma del cliente a pie de tajo.',
        },
        {
          label: 'Proyectos',
          color: 'var(--ts-piece-proyectos)',
          desc: 'Cada obra con su presupuesto, sus costes y su avance, comparados en tiempo real.',
        },
      ],
      aMedida: {
        label: 'A su medida',
        desc: 'Cuadro de mando de obras e incidencias, hecho para su forma de dirigir: estado, desvíos y alertas por obra.',
      },
      capturas: [
        {
          image: 'assets/img/soluciones/partes-trabajo.png',
          caption: 'El parte del día: horas, materiales y firma, desde la obra.',
        },
        {
          image: 'assets/img/soluciones/partes-trabajo-2.png',
          caption: 'Cada parte firmado actualiza el coste de su obra al momento.',
        },
      ],
      resultado:
        'De facturar la semana de hace un mes a facturar la semana en curso — y de enterarse de los desvíos al cierre, a verlos el mismo día.',
    },
    subtitle: 'Software a medida para empresa constructora',
    location: 'Construcción',
    category: 'Todo Sector + Módulos específicos',
    year: '2025',
    area: 'Angular · Express · PostgreSQL',
    description:
      'Aplicación web a medida para el seguimiento y control de proyectos de construcción. Cubre desde la apertura de un proyecto hasta el cierre con el cliente: partes de obra diarios, control de materiales, asignación de operarios y firma digital en campo desde dispositivos móviles.\n\nEl equipo de dirección dispone de un cuadro de mando en tiempo real con el estado de cada obra, incidencias abiertas y comparativa de costes vs presupuesto. La firma digital integrada elimina el papel en la entrega de partes y acelera la facturación.',
    heroImage: 'assets/img/unsplash-metodologia.jpg',
    images: [
      {
        src: 'assets/img/unsplash-metodologia.jpg',
        thumbnail: 'assets/img/unsplash-metodologia.jpg',
        alt: 'Cuadro de mando de obras',
      },
      {
        src: 'assets/img/unsplash-hero-office.jpg',
        thumbnail: 'assets/img/unsplash-hero-office.jpg',
        alt: 'Gestión de proyectos',
      },
      {
        src: 'assets/img/unsplash-contabilidad.jpg',
        thumbnail: 'assets/img/unsplash-contabilidad.jpg',
        alt: 'Control de costes',
      },
    ],
  },

  // ── Portal de expedientes ────────────────────────────────────────────
  {
    slug: 'portal-expedientes-gestoria',
    title: 'Portal de Expedientes',
    tipo: 'Plataforma + web propia',
    variante: 'solid',
    cardImage: 'assets/img/soluciones/backlog.png',
    resumen:
      'Cada cliente consulta sus expedientes y firma desde casa; los plazos legales avisan solos. Menos llamadas, menos olvidos, menos papel.',
    ficha: {
      heroTitle: 'Los plazos avisan solos. El cliente aporta y firma desde casa.',
      heroSubtitle:
        'Una asesoría con cientos de expedientes centralizó su operativa: cada cliente consulta y firma desde su portal, y los vencimientos legales saltan antes de que se echen encima.',
      solutionSlug: 'gestorias-asesorias',
      sectorLabel: 'Gestorías',
      heroImage: 'assets/img/soluciones/backlog.png',
      necesitaban:
        'Los expedientes vivían en carpetas y correos; los plazos, en la cabeza de cada gestor. Una firma era una visita, y un olvido de vencimiento, un problema.',
      montamos:
        'La gestión completa de sus expedientes sobre la plataforma: portal del cliente para consultar y firmar a distancia, y un seguimiento de plazos que avisa solo, con su marca y sus tipos de expediente.',
      dentroIntro:
        'Módulos del catálogo más las piezas construidas a medida para su despacho. Todo sobre la misma plataforma: sin migrar datos, sin sistemas paralelos.',
      baseDesc:
        'Fichaje, facturación, clientes y gastos. La base sobre la que se apoyan los expedientes y la firma.',
      modulos: [
        {
          label: 'Portal del cliente',
          color: 'var(--ts-piece-portal)',
          desc: 'Cada cliente consulta sus documentos, aporta lo que le pides y firma desde casa.',
        },
        {
          label: 'Proyectos',
          color: 'var(--ts-piece-proyectos)',
          desc: 'Cada expediente con sus tareas y plazos; los vencimientos avisan solos.',
        },
      ],
      aMedida: {
        label: 'A su medida',
        desc: 'Tipos de expediente y flujos propios del despacho, con alertas de plazo hechas a su forma de trabajar.',
      },
      capturas: [
        {
          image: 'assets/img/soluciones/backlog.png',
          caption: 'Cada expediente con su checklist de plazos y tareas.',
        },
        {
          image: 'assets/img/soluciones/clientes.png',
          caption: 'El cliente aporta y firma desde su portal, sin desplazarse.',
        },
      ],
      resultado:
        'De perseguir a cada cliente por email y vigilar plazos a mano a que el expediente avise solo — menos llamadas, menos olvidos, menos papel.',
    },
    subtitle: 'SaaS Todo Sector para gestorías y asesorías',
    location: 'Gestoría / Asesoría',
    category: 'Todo Sector + Web',
    year: '2024',
    area: 'Angular SSR · Node.js · MySQL',
    description:
      'Plataforma Todo Sector más módulos para gestorías que permite centralizar expedientes de clientes, controlar plazos legales y automatizar notificaciones. Personalizado con su marca, sus tipos de expediente configurables y su flujo de trabajo.\n\nEl módulo de firma electrónica integrado permite que los clientes finales firmen documentos desde cualquier dispositivo sin necesidad de desplazarse a la oficina. Las alertas de vencimiento y las tareas asignadas a cada gestor reducen los errores por olvido y mejoran la productividad del equipo.',
    heroImage: 'assets/img/unsplash-contabilidad.jpg',
    images: [
      {
        src: 'assets/img/unsplash-contabilidad.jpg',
        thumbnail: 'assets/img/unsplash-contabilidad.jpg',
        alt: 'Gestión de expedientes',
      },
      {
        src: 'assets/img/unsplash-tributaria.jpg',
        thumbnail: 'assets/img/unsplash-tributaria.jpg',
        alt: 'Módulo tributario',
      },
      {
        src: 'assets/img/unsplash-juridico.jpg',
        thumbnail: 'assets/img/unsplash-juridico.jpg',
        alt: 'Documentación jurídica',
      },
    ],
  },

  // ── App instaladores ─────────────────────────────────────────────────
  {
    slug: 'app-rutas-instaladores',
    title: 'App de Instaladores',
    tipo: 'Módulos hechos a medida',
    variante: 'outline',
    cardImage: 'assets/img/soluciones/ubicaciones.png',
    resumen:
      'La ruta del día en el móvil del técnico: presupuesto delante del cliente, firma al terminar y la oficina facturando sin esperar a que vuelva.',
    ficha: {
      heroTitle:
        'La ruta del día en el móvil del técnico. La oficina factura sin esperar.',
      heroSubtitle:
        'Una empresa de instalaciones con equipos en campo montó su operativa en el móvil: ruta, presupuesto delante del cliente y firma al terminar, sincronizado con la oficina al momento.',
      solutionSlug: 'servicios-tecnicos',
      sectorLabel: 'Servicios técnicos',
      heroImage: 'assets/img/soluciones/ubicaciones.png',
      necesitaban:
        'El técnico volvía a la oficina con partes en papel y presupuestos por hacer. Facturar un trabajo llevaba días, y el cliente esperaba para firmar y para su factura.',
      montamos:
        'Una app para el equipo de campo sobre la plataforma: ruta del día, presupuesto y trabajo aprobado en el sitio, firma del cliente al terminar y todo sincronizado con la oficina para facturar sin esperas.',
      dentroIntro:
        'Módulos del catálogo más las piezas construidas a medida para cómo se mueve su equipo. Todo sobre la misma plataforma: sin migrar datos, sin sistemas paralelos.',
      baseDesc:
        'Fichaje, facturación, clientes y gastos. Cada técnico ficha desde su móvil, sin licencia por cabeza.',
      modulos: [
        {
          label: 'Partes de trabajo',
          color: 'var(--ts-piece-partes)',
          desc: 'Órdenes de trabajo, materiales y horas por servicio, desde el móvil del técnico.',
        },
      ],
      aMedida: {
        label: 'A su medida',
        desc: 'La ruta del día y el flujo presupuesto → trabajo → firma, montados para cómo trabaja su equipo en calle.',
      },
      capturas: [
        {
          image: 'assets/img/soluciones/ubicaciones.png',
          caption: 'La ruta del día del técnico, con sus avisos y su historial.',
        },
        {
          image: 'assets/img/soluciones/tareas-por-cobrar.png',
          caption: 'Trabajos cerrados y listos para facturar, sin pasar a limpio.',
        },
      ],
      resultado:
        'De facturar a fin de semana lo de toda la semana a facturar el mismo día que se cierra el trabajo — con la firma del cliente ya dentro.',
    },
    subtitle: 'Gestión de rutas, presupuestos y firma en campo',
    location: 'Instaladores',
    category: 'Todo Sector + Módulos específicos',
    year: '2024',
    area: 'Angular PWA · Node.js · MySQL',
    description:
      'Aplicación PWA (Progressive Web App) orientada a empresas de instalaciones (climatización, electricidad, fontanería) con equipos que trabajan en campo. El día empieza con la ruta asignada en el móvil, los detalles del cliente y el historial de intervenciones anteriores.\n\nDesde el propio dispositivo el instalador puede generar un presupuesto, convertirlo en trabajo aprobado, registrar materiales usados y obtener la firma del cliente al terminar. Todo queda sincronizado automáticamente con la oficina, que puede facturar sin esperar a que el equipo vuelva.',
    heroImage: 'assets/img/unsplash-hero-office.jpg',
    images: [
      {
        src: 'assets/img/unsplash-hero-office.jpg',
        thumbnail: 'assets/img/unsplash-hero-office.jpg',
        alt: 'Panel de gestión de instaladores',
      },
      {
        src: 'assets/img/unsplash-metodologia.jpg',
        thumbnail: 'assets/img/unsplash-metodologia.jpg',
        alt: 'Planificación de rutas',
      },
      {
        src: 'assets/img/unsplash-contabilidad.jpg',
        thumbnail: 'assets/img/unsplash-contabilidad.jpg',
        alt: 'Facturación automática',
      },
    ],
  },

  // ── Sistema de reservas ──────────────────────────────────────────────
  {
    slug: 'sistema-reservas-restaurante',
    title: 'Sistema de Reservas',
    tipo: 'Plataforma TodoSector',
    variante: 'solid',
    cardImage: 'assets/img/soluciones/ts-prodcutos.png',
    resumen:
      'Mesas, reservas y comandas en una pantalla. El comensal reserva desde la web del restaurante y las no-presentaciones bajan con los recordatorios automáticos.',
    ficha: {
      heroTitle: 'Sala, reservas y comandas en una pantalla. La cuenta sale sola.',
      heroSubtitle:
        'Un restaurante con reservas conectó su servicio: mesas y comandas en una misma pantalla, reserva online desde su web y recordatorios que bajan las no-presentaciones.',
      solutionSlug: 'restauracion',
      sectorLabel: 'Restauración',
      heroImage: 'assets/img/soluciones/ts-prodcutos.png',
      necesitaban:
        'Las reservas se apuntaban en una libreta y las comandas iban a voz. Las no-presentaciones dejaban mesas vacías y el cierre se cuadraba a mano, ticket a ticket.',
      montamos:
        'La gestión de sala sobre la plataforma: mesas y comandas en una pantalla, reservas online desde su web con recordatorio automático, y cada servicio conectado con la facturación.',
      dentroIntro:
        'Módulos del catálogo más las piezas construidas a medida para su local. Todo sobre la misma plataforma: sin migrar datos, sin sistemas paralelos.',
      baseDesc:
        'Fichaje, facturación, clientes y gastos. La base sobre la que se apoyan la sala y el cobro.',
      modulos: [
        {
          label: 'Restauración',
          color: 'var(--ts-piece-restauracion)',
          desc: 'Reservas, mesas y comandas en un mismo sitio, conectadas con la facturación.',
        },
      ],
      aMedida: {
        label: 'A su medida',
        desc: 'La web de reservas del restaurante y el mapa de sala, montados para su local y su forma de servir.',
      },
      capturas: [
        {
          image: 'assets/img/soluciones/ts-prodcutos.png',
          caption: 'El TPV de sala: comandas por mesa, conectadas con la cuenta.',
        },
        {
          image: 'assets/img/soluciones/calendario.png',
          caption: 'Reservas del turno y recordatorios automáticos al comensal.',
        },
      ],
      resultado:
        'De la libreta de reservas y el cuadre a mano a una sala conectada — menos no-presentaciones y un cierre que cuadra solo.',
    },
    subtitle: 'Gestión de mesas, reservas y comandas para restaurante',
    location: 'Restauración',
    category: 'SaaS',
    year: '2024',
    area: 'Angular · Node.js · MySQL',
    description:
      'Software de gestión integral para restaurantes que unifica en una sola pantalla el estado de las mesas, las reservas del día y las comandas en curso. El personal de sala gestiona el servicio desde tablets sin necesidad de ir a la barra, mientras cocina recibe los pedidos en tiempo real.\n\nEl módulo de reservas online permite a los comensales reservar directamente desde la web del restaurante (con recordatorio automático por email/SMS), reduciendo las no-shows y optimizando la ocupación del local.',
    heroImage: 'assets/img/luke-heibert-gthSas4oYC0-unsplash@1920.jpg',
    images: [
      {
        src: 'assets/img/luke-heibert-gthSas4oYC0-unsplash@1920.jpg',
        thumbnail: 'assets/img/luke-heibert-gthSas4oYC0-unsplash@1920.jpg',
        alt: 'Gestión del restaurante',
      },
      {
        src: 'assets/img/unsplash-hero-office.jpg',
        thumbnail: 'assets/img/unsplash-hero-office.jpg',
        alt: 'Panel de reservas',
      },
      {
        src: 'assets/img/unsplash-metodologia.jpg',
        thumbnail: 'assets/img/unsplash-metodologia.jpg',
        alt: 'Gestión de mesas',
      },
    ],
  },

  // ── Seguros de buceo ─────────────────────────────────────────────────
  {
    slug: 'plataforma-seguros-buceo',
    title: 'Plataforma de Seguros',
    tipo: 'Desarrollo completo',
    variante: 'outline',
    cardImage: 'assets/img/soluciones/facturas.png',
    resumen:
      'Altas de socios, renovaciones automáticas, venta de pólizas y cobro online para una correduría y sus centros afiliados — cada centro ve solo lo suyo.',
    ficha: {
      heroTitle:
        'Altas, renovaciones y pólizas, cada centro con lo suyo. Todo en un backoffice.',
      heroSubtitle:
        'Una correduría de seguros de buceo y sus centros afiliados operan sobre una misma plataforma a medida: altas de socios, renovaciones automáticas, venta de pólizas y cobro online, cada centro viendo solo lo suyo.',
      heroImage: 'assets/img/soluciones/facturas.png',
      necesitaban:
        'Socios, pólizas y renovaciones vivían en hojas y correos, y cada centro afiliado necesitaba ver lo suyo sin ver lo de los demás. Renovar y cobrar era un trabajo manual y repetitivo.',
      montamos:
        'Una plataforma completa a medida, multitenant: altas de socios, renovaciones automáticas, venta de pólizas y cobro online con TPV, más su web integrada con el alta. Cada centro accede con sus credenciales y ve solo sus socios.',
      dentroIntro:
        'Una base común y, sobre ella, toda la operativa de la correduría construida a medida. Todo sobre la misma plataforma: sin sistemas paralelos.',
      baseDesc:
        'Facturación, clientes y gastos como base; encima, la operativa de pólizas y cobros de la correduría.',
      modulos: [
        {
          label: 'Portal por centro',
          color: 'var(--ts-piece-portal)',
          desc: 'Cada centro afiliado accede a lo suyo: sus socios, sus pólizas, sus cobros — aislado del resto.',
        },
        {
          label: 'Web y CMS',
          color: 'var(--ts-piece-web)',
          desc: 'La web corporativa integrada con el alta: los socios inician la contratación desde fuera.',
        },
      ],
      aMedida: {
        label: 'A su medida',
        desc: 'El núcleo de la correduría: altas, renovaciones automáticas, venta de pólizas y TPV de cobro, hechos a medida de su negocio.',
      },
      capturas: [
        {
          image: 'assets/img/soluciones/facturas.png',
          caption: 'Pólizas y cobros en un mismo backoffice.',
        },
        {
          image: 'assets/img/soluciones/clientes.png',
          caption: 'Cada centro con sus socios, aislado del resto.',
        },
      ],
      resultado:
        'De renovar y cobrar a mano, póliza a póliza, a un backoffice donde las renovaciones y los cobros van solos — y cada centro ve exactamente lo suyo.',
    },
    subtitle:
      'Plataforma de gestión de seguros de buceo + web corporativa',
    location: 'Seguros de buceo',
    category: 'SaaS y Web a medida',
    year: '2024',
    area: 'Angular · Node.js · MySQL · TPV integrado',
    description:
      'Plataforma de gestión de seguros de buceo para una correduría especializada, desarrollada completamente a medida. Centraliza en un único backoffice multitenant toda la operativa de la empresa: altas de socios, renovaciones automáticas, venta de pólizas y descarga instantánea de contratos y documentos.\n\nEl sistema integra un TPV para cobros online, un módulo de campañas de email marketing, gestión de centros de buceo afiliados, facturación, reportes mensuales y un potente panel de avisos y recordatorios automáticos a clientes. Cada centro de buceo accede con sus propias credenciales y visualiza únicamente sus socios y sus datos.\n\nAdemás del gestor, se desarrolló la web corporativa de la empresa, integrada con el sistema de altas para que los centros y los socios puedan iniciar el proceso de contratación directamente desde la web.',
    heroImage: 'assets/img/unsplash-juridico.jpg',
    images: [
      {
        src: 'assets/img/unsplash-juridico.jpg',
        thumbnail: 'assets/img/unsplash-juridico.jpg',
        alt: 'Panel de gestión de seguros',
      },
      {
        src: 'assets/img/unsplash-hero-office.jpg',
        thumbnail: 'assets/img/unsplash-hero-office.jpg',
        alt: 'Gestión de centros de buceo',
      },
      {
        src: 'assets/img/unsplash-contabilidad.jpg',
        thumbnail: 'assets/img/unsplash-contabilidad.jpg',
        alt: 'Facturación y reportes',
      },
    ],
  },
];
